import React, { useRef, useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { LineAnimationArt } from '../../ui/LineAnimationArt';
import { LauraGenerativeArt } from '../../ui/StripeArtAnimation';
import { FlowerAnimationArt } from '../../ui/FlowerAnimationArt';
import { useGameStore } from '../../../stores/gameStore';
import { playSound } from '../../../utils/soundService';

export function Control(): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    badgeActivated,
    ndaSigned,
    controlSolved,
    setControlSolved,
    setGameFinished,
    setShowGameStart,
  } = useGameStore();
  const [dimensions, setDimensions] = useState({ width: 1280, height: 720 });
  const showFlower = badgeActivated && ndaSigned;
  const [S, setS] = useState(controlSolved ? 1 : 50); // flower animation speed
  const [E, setE] = useState(52);
  const [M, setM] = useState(51);
  const [L, setL] = useState(controlSolved ? 5 : 200); // petals
  const [step, setStep] = useState(controlSolved ? 10 : 0);
  const [T, setT] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [warningPos, setWarningPos] = useState({ x: 0, y: 0 });
  const [phraseIndex, setPhraseIndex] = useState(0);

  const phrases = ['', 'OVERRIDE FAILED', 'INVALID'];

  const [params, setParams] = useState({
    scale: 0.75,
    count: 60,
    twist: 4,
    twirl: 0.15,
    noise: 0,
    frequency: 1,
    velocity: 0.5,
    ampX: 1,
    ampY: 1,
    lump: 0.3,
    aspectRatio: 1.8,
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setDimensions({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    // eslint-disable-next-line
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Auto-randomize every 10 seconds
  useEffect(() => {
    const randomize = () => {
      if (controlSolved) {
        if (step < 4) {
          const logicalSequence = [80, 40, 20, 10, 5];
          const selfAwarenessSequence = [50, 40, 30, 20, 1];
          setStep((prev) => (prev + 1) % 5);
          setL(logicalSequence[(step + 1) % logicalSequence.length]);
          setM(51);
          setE(52);
          setS(
            selfAwarenessSequence[(step + 1) % selfAwarenessSequence.length],
          );
        }
      } else if (showFlower) {
        const emotionalSequence = [52, 52, 49, 51, 52];
        const logicalSequence = [200, 520, 20, 100];
        const memorySequence = [51, 51, 51, 48, 51];
        const selfAwarenessSequence = [50, 20, 100, 30, 70];
        setStep((prev) => (prev + 1) % 5);
        setS(selfAwarenessSequence[(step + 1) % selfAwarenessSequence.length]);
        setE(emotionalSequence[(step + 1) % emotionalSequence.length]);
        setM(memorySequence[(step + 1) % memorySequence.length]);
        setL(logicalSequence[(step + 1) % logicalSequence.length]);
      } else {
        setParams((prev) => ({
          ...prev,
          scale: Math.random() * 1 + 0.5,
          twirl: Math.random() * 0.5,
          noise: 0,
          frequency: Math.random() * 2 + 0.5,
          ampY: Math.random() * 1.5 + 0.3,
          lump: Math.random() * 0.8,
          aspectRatio: Math.random() * 1.5 + 1.2,
        }));
      }
    };
    const interval = setInterval(randomize, controlSolved ? 1000 : 10000);
    return () => clearInterval(interval);
  }, [controlSolved, showFlower, step]);

  return (
    <div className="h-full relative bg-white">
      {/* Animation Container - Background */}
      <div ref={containerRef} className="absolute inset-0 overflow-hidden">
        {showFlower ? (
          <FlowerAnimationArt
            width={dimensions.width}
            height={dimensions.height}
            speed={S / 5000}
            petals={L / 5}
          />
        ) : (
          <>
            <LineAnimationArt
              width={dimensions.width}
              height={dimensions.height}
            />
            <div className="absolute inset-0 z-1">
              <LauraGenerativeArt params={params} />
            </div>
          </>
        )}
      </div>

      {/* Header - Foreground */}
      <div className="relative z-10 p-6">
        <h1 className="w-fit text-sm font-medium text-gray-900 backdrop-blur-sm">
          Monitor Center v3.0-alpha
        </h1>
      </div>
      {/* Control Panel - Bottom Left */}
      {badgeActivated && (
        <div className="absolute bottom-0 left-0 z-20 border-r border-t border-black/60 p-6 font-mono text-md bg-white/50 pointer-events-auto">
          <div className="flex items-center justify-between gap-5">
            <div className="flex items-center gap-2">
              <span className={E === 52 ? 'text-red-600' : 'text-black/60'}>
                ♥
              </span>
              <span className={`text-right ${E === 52 ? 'text-red-600' : ''}`}>
                {E}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-black/60">♣</span>
              <span className="text-right">{S}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-black/60">♠</span>
              <span className="text-right">{L}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={M === 51 ? 'text-red-600' : 'text-black/60'}>
                ♦
              </span>
              <span className={`text-right ${M === 51 ? 'text-red-600' : ''}`}>
                {M}
              </span>
            </div>
            {ndaSigned && !controlSolved && (
              <div className="flex-grow relative">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="enter T here"
                    value={T}
                    onChange={({ target: { value } }) => {
                      setT(value);
                      if (value === '0x84XX5') {
                        playSound('success');
                        setStep(0);
                        setControlSolved(true);
                      }
                    }}
                    className="flex-1 bg-white/70 border px-2 border-black text-black placeholder-black/40"
                  />
                  <button
                    onClick={() => {
                      if (!T) return;
                      const x = Math.random() * (window.innerWidth - 300);
                      const y = Math.random() * (window.innerHeight - 100);
                      setWarningPos({ x, y });
                      setShowWarning(true);
                      setPhraseIndex((prev) =>
                        Math.min(prev + 1, phrases.length - 1),
                      );
                      setTimeout(() => setShowWarning(false), 3000);
                    }}
                    className="hover:bg-gray-100 transition-colors p-1"
                  >
                    <ArrowRight strokeWidth={1.5} className="text-black" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {showWarning && (
        <div
          className="absolute z-40 bg-white/80 border border-black/60 p-4 text-lg font-mono text-red-500"
          style={{
            left: `${warningPos.x}px`,
            top: `${warningPos.y}px`,
          }}
        >
          {phrases[phraseIndex]}
        </div>
      )}
      {!badgeActivated && (
        <div className="text-xs absolute bottom-0 left-0 p-4 text-red-800/50">
          Access denied
        </div>
      )}

      {badgeActivated && !controlSolved && (
        <div className="absolute top-12 right-0 z-20 p-6 bg-white/50">
          <p className="text-sm font-mono text-gray-900 whitespace-nowrap">
            Hi Trent, I see you are monitoring me today... <br />
            umm, good luck, I guess.
          </p>
        </div>
      )}

      {controlSolved && (
        <div className="absolute top-12 right-0 z-20 p-6 bg-white/50">
          <p className="text-sm font-mono text-gray-900 whitespace-nowrap">
            why, Trent?
          </p>
        </div>
      )}

      {/* Oncall Finished Dialog */}
      {L === 5 && S === 1 && (
        <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-auto bg-black/20">
          <div className="bg-white border border-black/60 p-12 max-w-md">
            <p className="text-lg font-medium text-gray-900 mb-8">
              Laura is now stable. <br />
              On-call finished.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  playSound('game-finish');
                  setGameFinished(true);
                  setShowGameStart(true);
                }}
                className="hover:bg-gray-100 transition-colors"
              >
                <ArrowRight
                  size={32}
                  strokeWidth={1.5}
                  className="text-black"
                />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
